import Info from '@mui/icons-material/Info'
import { Alert, Box, Button, Tooltip } from '@mui/material'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { type Farm, type SOCSamplesLoadResponse } from '../../../types/apiModels'
import { useProcessLoading } from '../../../utils/Loading/useProcessLoading'

const SOCSAMPLES_LOAD_DATA_URL = import.meta.env.VITE_SOCSAMPLES_LOAD_DATA_URL

interface LoadDataButtonProps {
  farm: Farm
  readOnly: boolean
  hasCompletedSOCActivities: boolean
  onDataLoaded: (data: SOCSamplesLoadResponse) => void
}

const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

const LoadDataButton: React.FC<LoadDataButtonProps> = ({
  farm,
  readOnly,
  hasCompletedSOCActivities,
  onDataLoaded,
}) => {
  const { processLoading } = useProcessLoading()
  const { watch } = useFormContext()
  const [farmNameWarning, setFarmNameWarning] = useState<{
    farmNameFromFile: string
    currentFarmName: string
  } | null>(null)

  const laboratoryResults = watch('laboratoryResults')
  const laboratoryId = watch('laboratoryId')

  const handleLoadData = async () => {
    if (!laboratoryResults || laboratoryResults.length === 0) {
      return
    }

    await processLoading({
      loadingMessage: 'Procesando documento...',
      successfulMessage: 'Datos cargados exitosamente',
      doAction: async () => {
        try {
          const fileUrl = laboratoryResults[0]
          const fileName = fileUrl.split('/').pop() || ''

          if (!SOCSAMPLES_LOAD_DATA_URL) {
            throw new Error('SOCSAMPLES_LOAD_DATA_URL is not defined')
          }

          if (laboratoryId === undefined || laboratoryId === null) {
            throw new Error('Laboratorio no seleccionado')
          }

          const response = await fetch(SOCSAMPLES_LOAD_DATA_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              farmId: farm.id,
              fileName,
              laboratoryId,
            }),
          })

          if (!response.ok) {
            const json = await response.json()

            throw new Error(`Error ${response.status}: ${json?.message || response.statusText}`)
          }

          const result: SOCSamplesLoadResponse = await response.json()

          if (result.socSamples?.farmName) {
            const normalizedFileFarmName = normalizeString(result.socSamples.farmName)
            const normalizedCurrentFarmName = normalizeString(farm.name)

            if (normalizedFileFarmName !== normalizedCurrentFarmName) {
              setFarmNameWarning({
                farmNameFromFile: result.socSamples.farmName,
                currentFarmName: farm.name,
              })
            } else {
              setFarmNameWarning(null)
            }
          }

          onDataLoaded(result)
        } catch (error) {
          console.error('Error al procesar el documento:', error)
          throw error
        }
      },
    })
  }

  const isLoadDataButtonEnabled =
    hasCompletedSOCActivities &&
    laboratoryResults &&
    laboratoryResults.length > 0 &&
    laboratoryId !== undefined &&
    laboratoryId !== null

  return (
    <Box sx={{ marginBottom: 2, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
        <Tooltip
          title={
            !hasCompletedSOCActivities
              ? 'No se encontraron actividades SOC con sitios confirmados para cargar certificado de análisis de suelo'
              : !isLoadDataButtonEnabled
                ? 'Seleccione el laboratorio y cargue el archivo de resultados para cargar los datos.'
                : ''
          }
        >
          <span>
            <Button
              disabled={!isLoadDataButtonEnabled || readOnly}
              variant="contained"
              onClick={handleLoadData}
            >
              Cargar datos
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Este proceso puede demorar hasta 1 minuto.">
          <Info color="action" sx={{ cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      {farmNameWarning && (
        <Alert severity="warning" sx={{ width: '100%' }}>
          Nombre de establecimiento detectado en el archivo ({farmNameWarning.farmNameFromFile}) no
          coincide con el establecimiento actual ({farmNameWarning.currentFarmName})
        </Alert>
      )}
    </Box>
  )
}

export default LoadDataButton
